import { Color, Spacing } from '@persona-id/design-tokens';
import { groupBy } from 'lodash/fp';
import { DateTime } from 'luxon';
import { Fragment, useState } from 'react';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import ContextMenu from 'dashboard/components/ContextMenu';
import Permissioned from 'dashboard/components/Permissioned';
import IgnoreHitsModal from 'dashboard/components/ReportResult/IgnoreHitsModal';
import { MatchIconPositive } from 'dashboard/components/ReportResult/MatchIcons';
import PoliticallyExposedPersonPersonaV2 from 'dashboard/components/ReportResult/PoliticallyExposedPersonPersonaV2/PoliticallyExposedPersonPersonaV2';
import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import { FlagIcon } from 'dashboard/components-lib/FlagIcon/FlagIcon';
import {
  pepClassDisplayText,
  PoliticallyExposedPersonClass,
  PoliticallyExposedPersonInfoField,
  PoliticallyExposedPersonMatch,
  PoliticallyExposedPersonSource,
  ReportPoliticallyExposedPerson,
} from 'dashboard/models/report/politically-exposed-person';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import TabBar, { Tab } from 'design-system/components/TabBar';
import { Paragraph, Span } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';
import { BASE } from 'design-system/variables/spacing';
import { FontWeight } from 'design-system/variables/text';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

const MatchHeadingTitle = styled(Paragraph)`
  font-weight: 500;
  display: flex;
  align-items: center;
`;

const MatchHeadingSticker = styled(Sticker)`
  margin-left: ${Spacing[2]};
`;

const StyledSticker = styled(Sticker)`
  margin-right: ${Spacing[2]};
`;

export const MatchesAccordion = styled(Accordion)`
  &:not(:last-child) {
    border-bottom: 1px solid ${Color.CoreSlate100};
  }
`;

const MatchSource = styled(Paragraph)`
  display: grid;
  grid-column-gap: ${Spacing[2]};
  grid-template-columns: ${Spacing[5]} 1fr;
`;

export const MatchDetails = styled('section')`
  border-radius: ${({ theme }) => theme.card.borderRadius};
  padding: ${Spacing[3]};
  background-color: ${({ theme }) => theme.color.background.secondary};
`;

const PositionsContainer = styled('div')`
  margin-left: ${BASE * 3.5}px;
  ${paragraphMixins.md}
`;

const RelatedUrlsAccordion = styled(Accordion)`
  margin-left: ${Spacing[5]};
`;

const StyledLink = styled.a`
  display: block;
  margin-left: ${Spacing[5]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: break-word;
`;

const StyledHeaderLink = styled(StyledLink)`
  margin-left: ${Spacing[2]};
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const TabText = styled.div`
  font-weight: ${FontWeight.Medium};
  & > span {
    margin-right: ${Spacing[2]};
  }
`;

const INTERNAL_SOURCE_SLUG = 'internal-data-source';
const PERSONA = 'persona';

interface PoliticallyExposedPersonProps {
  report: ReportPoliticallyExposedPerson;
  multiCardFormat: boolean;
  environmentName?: string;
}

const PoliticallyExposedPerson = ({
  report,
  multiCardFormat,
  environmentName,
}: PoliticallyExposedPersonProps) => {
  const { result: result, 'ignore-list': ignoreList } = report.attributes;

  // Default to true so that we can properly format the inputs
  let isPersonaReport = true;

  if (result?.length > 0) {
    isPersonaReport = result[0]['data-source'] === PERSONA;
  } else if (ignoreList && ignoreList.length > 0) {
    isPersonaReport = ignoreList?.[0].match['data-source'] === PERSONA;
  }

  if (isPersonaReport) {
    // TODO Change name back to PoliticallyExposedPersonPersona once fully migrated
    return (
      <PoliticallyExposedPersonPersonaV2
        report={report}
        multiCardFormat={true}
        environmentName={environmentName}
      />
    );
  }

  return (
    <PoliticallyExposedPersonComplyAdvantage
      report={report}
      multiCardFormat={multiCardFormat}
      environmentName={environmentName}
    />
  );
};

interface PoliticallyExposedPersonProps {
  report: ReportPoliticallyExposedPerson;
  multiCardFormat: boolean;
  environmentName?: string;
}

const PoliticallyExposedPersonComplyAdvantage = ({
  report,
  multiCardFormat,
  environmentName,
}: PoliticallyExposedPersonProps) => {
  const [ignoreHitsModal, { enable: showIgnoreHitsModal, disable: hideIgnoreHitsModal }] =
    useToggle(false);
  const [isActiveTabSelected, setIsActiveTabSelected] = useState(true);

  const { result: result, 'ignore-list': ignoreList = [] } = report.attributes;

  const displayedResults = isActiveTabSelected ? result : ignoreList?.map((entry) => entry.match);

  const displayedResultsContent =
    displayedResults?.length > 0 ? (
      displayedResults.map((match) => (
        <>
          <MatchesAccordion
            key={`${match.name}-${match.birthdates?.join('-')}`}
            menuItem={true}
            menuItemBorder={false}
            menuItemHover={false}
            caretSize='md'
            padded={false}
            defaultExpanded={true}
            titleContainerHeight='auto'
            title={<MatchHeading match={match} />}
          >
            <MatchDetails>
              <MatchPositions match={match} />
            </MatchDetails>
            <Spacer height={2} />
          </MatchesAccordion>
          <Spacer height={1} />
        </>
      ))
    ) : (
      <MatchIconPositive label={`No ${isActiveTabSelected ? 'active' : 'dismissed'} matches`} />
    );

  let content;
  if (result == null || (result.length === 0 && ignoreList?.length === 0)) {
    content = <MatchIconPositive label='No matches found.' />;
  } else {
    content = (
      <>
        {multiCardFormat && (
          <TitleContainer>
            <Card.Title>Results</Card.Title>
            {result.length > 0 && (
              <Permissioned permission={Permission.ReportIgnoreHitsWrite}>
                <ContextMenu
                  variant='inverse'
                  items={[
                    {
                      id: 'reports-ignore-hits-modal__dismiss-hits-button',
                      onClick: showIgnoreHitsModal,
                      hidden: false,
                      label: 'Dismiss matches',
                    },
                  ]}
                  renderItem={(item) => (
                    <ContextMenu.Item key={item.id} id={item.id} onClick={item.onClick}>
                      {item.label}
                    </ContextMenu.Item>
                  )}
                />
                <IgnoreHitsModal
                  report={report}
                  ignoreHitsModalOpen={ignoreHitsModal}
                  hideIgnoreHitsModal={hideIgnoreHitsModal}
                />
              </Permissioned>
            )}
          </TitleContainer>
        )}
        {ignoreList.length > 0 && (
          <>
            <TabBar>
              <Tab
                id='report-watchlist__ignored-tab-active'
                selected={isActiveTabSelected}
                onClick={() => {
                  setIsActiveTabSelected(true);
                }}
              >
                <TabText>
                  <span>Active</span>
                  <Sticker>{result.length}</Sticker>
                </TabText>
              </Tab>
              <Tab
                id='report-watchlist__ignored-tab-ignored'
                selected={!isActiveTabSelected}
                onClick={() => {
                  setIsActiveTabSelected(false);
                }}
              >
                <TabText>
                  <span>Dismissed</span>
                  <Sticker>{ignoreList.length}</Sticker>
                </TabText>
              </Tab>
            </TabBar>
            <Spacer height={2} />
          </>
        )}
        {displayedResultsContent}
      </>
    );
  }

  return (
    <ReportResultLayout multiCardFormat={multiCardFormat} dataTestKey={`pep-${environmentName}`}>
      {content}
    </ReportResultLayout>
  );
};

interface MatchHeadingProps {
  match: PoliticallyExposedPersonMatch;
}

export const MatchHeading = ({ match }: MatchHeadingProps) => {
  const matchType = match['match-types']?.[0];
  const pepClasses = Array.from(
    new Set(match['positions']?.map((position) => position['pep-class'])),
  ).sort();

  return (
    <>
      <MatchHeadingTitle color='primary'>
        <span>{match.name}</span>
        <MatchHeadingSticker>{(match['positions'] ?? []).length}</MatchHeadingSticker>
      </MatchHeadingTitle>
      <Spacer height={0.5} />
      <Paragraph color='tertiary' size='sm'>
        {[
          matchType ? getMatchTypeLabel(matchType) : matchType,
          formatAgeData(match.birthdates, match['death-dates']),
          match['country'],
        ]
          .filter((data) => data)
          .join(' • ')}
      </Paragraph>
      <Spacer height={0.5} />
      <div>
        {pepClasses.map((pepClass) => (
          <StyledSticker key={pepClass}>{pepClassDisplayText(pepClass)}</StyledSticker>
        ))}
      </div>
      <Spacer height={2} />
    </>
  );
};

interface MatchPositionsProps {
  match: PoliticallyExposedPersonMatch;
}

const PEP_CLASS_ORDER: PoliticallyExposedPersonClass[] = [
  'pep-class-1',
  'pep-class-2',
  'pep-class-3',
  'pep-class-4',
];

export const MatchPositions = ({
  match: { positions, sources, affiliations, 'related-urls': relatedUrls },
}: MatchPositionsProps) => {
  const groupedPositions = groupBy('pep-class', positions);
  const matchedPepClasses = PEP_CLASS_ORDER.filter((pepClass) =>
    Object.keys(groupedPositions).includes(pepClass),
  );
  const groupedRelatedUrls = groupBy('source-key', relatedUrls);

  return (
    <>
      {matchedPepClasses.map((pepClass, pepIndex) => (
        <Fragment key={pepClass}>
          <Accordion
            caretSize='md'
            padded={false}
            defaultExpanded={true}
            titleContainerHeight='auto'
            titleGapSize={`${Spacing[1]}`}
            title={
              <MatchHeadingTitle color='primary' size='sm'>
                {pepClassDisplayText(pepClass)}
              </MatchHeadingTitle>
            }
          >
            <PositionsContainer>
              {groupedPositions[pepClass].map((position, positionIndex) => {
                const sourceKey = position['source-key'];
                let source = sourceKey ? sources?.[sourceKey] : undefined;

                if (sourceKey === INTERNAL_SOURCE_SLUG || source == null) {
                  const url = groupedRelatedUrls[INTERNAL_SOURCE_SLUG]?.[0]?.name;
                  const affiliation = affiliations?.filter(
                    (aff) => aff['source-key'] === INTERNAL_SOURCE_SLUG,
                  )?.[0]?.name;
                  source = {
                    'flag-code': source?.['flag-code'],
                    name: affiliation ?? (url ? 'Source link' : 'Source not available'),
                    url,
                  };
                }

                const filteredRelatedUrls =
                  sourceKey &&
                  groupedRelatedUrls[sourceKey]?.filter(
                    (relatedUrl: PoliticallyExposedPersonInfoField) =>
                      relatedUrl.name !== source?.url,
                  );

                return (
                  <Fragment key={`${position.name}-${position['source-key']}`}>
                    <Spacer height={0.5} />
                    <MatchHeadingTitle color='primary' size='sm'>
                      {position.name}
                    </MatchHeadingTitle>
                    <MatchSource color='primary' size='sm'>
                      <FlagIcon flagCode={source['flag-code'] ?? 'persona'} />
                      {!source.url ? (
                        <Span color='secondary'>{source.name}</Span>
                      ) : (
                        <StyledHeaderLink
                          href={ensureScheme(source.url)}
                          target='_blank'
                          rel='noreferrer'
                        >
                          {source.name}
                        </StyledHeaderLink>
                      )}
                    </MatchSource>
                    {filteredRelatedUrls && filteredRelatedUrls.length > 0 && (
                      <RelatedUrlsAccordion
                        caretSize='sm'
                        padded={false}
                        defaultExpanded={false}
                        titleContainerHeight='auto'
                        titleGapSize='0px'
                        title={
                          <Paragraph color='primary' size='sm'>{`${
                            filteredRelatedUrls.length
                          } related url${filteredRelatedUrls.length === 1 ? '' : 's'}`}</Paragraph>
                        }
                      >
                        {filteredRelatedUrls.map((url: PoliticallyExposedPersonSource) =>
                          url.name ? (
                            <StyledLink
                              key={url.name}
                              href={ensureScheme(url.name)}
                              target='_blank'
                              rel='noreferrer'
                            >
                              {url.name}
                            </StyledLink>
                          ) : (
                            <></>
                          ),
                        )}
                      </RelatedUrlsAccordion>
                    )}
                    {positionIndex < groupedPositions[pepClass].length - 1 && (
                      <Spacer height={0.5} />
                    )}
                  </Fragment>
                );
              })}
            </PositionsContainer>
          </Accordion>
          {pepIndex < matchedPepClasses.length - 1 && <Spacer height={1} />}
        </Fragment>
      ))}
    </>
  );
};

const getMatchTypeLabel = (matchType: string) =>
  matchType === 'name_exact' ? 'Primary name' : 'AKA';

const formatAgeData = (birthdates?: string[], deathDates?: string[]) => {
  const birthdate = birthdates?.[0];
  const deathDate = deathDates?.[0];

  if (birthdate && deathDate) {
    const momentBirthDate = DateTime.fromISO(birthdate);
    const momentDeathDate = DateTime.fromISO(deathDate);
    return `${birthdate} to ${deathDate} (Died at age ${Math.floor(
      momentDeathDate.diff(momentBirthDate, 'years').years,
    )})`;
  } else if (birthdate) {
    return `Born ${birthdate}`;
  } else if (deathDate) {
    return `Died ${deathDate}`;
  } else {
    return null;
  }
};

const ensureScheme = (url: string) => {
  return url.startsWith('http') ? url : `https://${url}`;
};

export default PoliticallyExposedPerson;
